import { useState, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, TextField } from 'Components/ui';

// Preserves the dual-list panel's flat, minimal search strip (32px,
// bottom-border only, no radius, 12px font) instead of letting CLOSED_SX's
// standard boxed look take over — same layered-sx approach as VendorList's
// icon padding and ItemSelector's fixed width.
const listSearchSx = {
  '& .MuiOutlinedInput-input.MuiOutlinedInput-input': { height: '32px', fontSize: '12px', padding: '0 10px' },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none', borderBottom: '1px solid var(--vo-cloud)', borderRadius: 0 },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderBottom: '1px solid var(--vo-cloud)' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderBottom: '1px solid var(--color-brand)' },
};

// ─── Avatar helpers ───────────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  'var(--vo-avatar-grad-1)',
  'var(--vo-avatar-grad-2)',
  'var(--vo-avatar-grad-3)',
  'var(--vo-avatar-grad-4)',
  'var(--vo-avatar-grad-5)',
  'var(--vo-avatar-grad-6)',
  'var(--vo-avatar-grad-7)',
  'var(--vo-avatar-grad-8)',
];

const getGradient = name =>
  AVATAR_GRADIENTS[(name || ' ').charCodeAt(0) % AVATAR_GRADIENTS.length];

const getInitials = name => {
  const parts = (name || '?').trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : (name || '?').substring(0, 2).toUpperCase();
};

// ─── Styled components ────────────────────────────────────────────────────────

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`;

const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;


const ErrorMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-danger);
  margin: 2px 0 0;
`;

// Active toggle card
const ActiveCard = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: var(--vo-mist);
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 24px;
  user-select: none;
`;

const ActiveTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ActiveTitle = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink-2);
`;

const ActiveDescription = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  line-height: 1.4;
`;

// Section title for dual listbox
const SectionTitle = styled.h3`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0 0 12px;
`;

const RequiredStar = styled.span`
  color: var(--vo-ink-2);
`;

// Dual listbox grid
const DualListbox = styled.div`
  display: grid;
  grid-template-columns: 1fr 40px 1fr;
  column-gap: 10px;
  align-items: stretch;
  min-height: 200px;
`;

const ListPanel = styled.div`
  display: flex;
  flex-direction: column;
  border: 1.5px solid ${({ $right }) => ($right ? 'var(--vo-steel)' : 'var(--vo-line)')};
  border-radius: 10px;
  overflow: hidden;
`;

const ListPanelHeader = styled.div`
  padding: 8px 12px;
  background: var(--vo-mist);
  border-bottom: 1px solid var(--vo-cloud);
`;

const ListPanelLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: var(--vo-slate);
  text-transform: uppercase;
`;


const ListItems = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 140px;
  max-height: 200px;
  background: white;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  cursor: pointer;
  background: ${({ $highlighted, $right }) =>
    $highlighted ? 'var(--vo-capri-50)' : $right ? 'var(--vo-mist)' : 'white'};
  border-left: 2px solid ${({ $highlighted }) => ($highlighted ? 'var(--color-brand)' : 'transparent')};

  &:hover {
    background: var(--vo-capri-50);
  }
`;

const UserAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarInitials = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 9px;
  font-weight: 700;
  color: white;
  line-height: 1;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const UserName = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 10px;
  color: var(--vo-slate);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 8px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-steel);
`;

// Control buttons column
const ControlsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 4px;
`;

const CtrlBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: ${({ $primary }) => ($primary ? 'none' : '1.5px solid var(--vo-line)')};
  background: ${({ $primary }) => ($primary ? 'var(--color-brand)' : 'white')};
  color: ${({ $primary }) => ($primary ? 'white' : 'var(--vo-slate)')};
  font-size: ${({ $text }) => ($text ? '13px' : '14px')};
  font-family: 'Maven Pro', sans-serif;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;

  &:hover {
    background: ${({ $primary }) => ($primary ? 'var(--color-brand-strong)' : 'var(--vo-mist)')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const LoadingText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  text-align: center;
  padding: 24px 0;
`;

// ─── Dual listbox sub-component ───────────────────────────────────────────────

const UserDualList = ({ available, selected, onChange, isLoading, t }) => {
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [leftHighlighted, setLeftHighlighted] = useState(new Set());
  const [rightHighlighted, setRightHighlighted] = useState(new Set());

  const filteredLeft = useMemo(
    () =>
      available.filter(u =>
        (u.name || '').toLowerCase().includes(leftSearch.toLowerCase())
      ),
    [available, leftSearch]
  );

  const filteredRight = useMemo(
    () =>
      selected.filter(u =>
        (u.name || '').toLowerCase().includes(rightSearch.toLowerCase())
      ),
    [selected, rightSearch]
  );

  const toggleLeft = id => {
    setLeftHighlighted(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleRight = id => {
    setRightHighlighted(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const moveHighlightedRight = () => {
    const toMove = available.filter(u => leftHighlighted.has(u._id));
    const newAvailable = available.filter(u => !leftHighlighted.has(u._id));
    onChange([...selected, ...toMove], newAvailable);
    setLeftHighlighted(new Set());
  };

  const moveHighlightedLeft = () => {
    const toMove = selected.filter(u => rightHighlighted.has(u._id));
    const newSelected = selected.filter(u => !rightHighlighted.has(u._id));
    onChange(newSelected, [...available, ...toMove]);
    setRightHighlighted(new Set());
  };

  const moveAllRight = () => {
    onChange([...selected, ...available], []);
    setLeftHighlighted(new Set());
  };

  const moveAllLeft = () => {
    onChange([], [...available, ...selected]);
    setRightHighlighted(new Set());
  };

  if (isLoading) {
    return <LoadingText>{t('user:groups.form.loadingUsers')}</LoadingText>;
  }

  return (
    <DualListbox>
      {/* Left panel — Available */}
      <ListPanel>
        <ListPanelHeader>
          <ListPanelLabel>{t('user:groups.form.available')}</ListPanelLabel>
        </ListPanelHeader>
        <TextField
          fullWidth
          value={leftSearch}
          onChange={e => setLeftSearch(e.target.value)}
          placeholder={t('user:groups.form.searchPlaceholder')}
          sx={listSearchSx}
        />
        <ListItems>
          {filteredLeft.length === 0 ? (
            <EmptyList>—</EmptyList>
          ) : (
            filteredLeft.map(user => (
              <UserItem
                key={user._id}
                $highlighted={leftHighlighted.has(user._id)}
                onClick={() => toggleLeft(user._id)}
              >
                <UserAvatar $gradient={getGradient(user.name)}>
                  <AvatarInitials>{getInitials(user.name)}</AvatarInitials>
                </UserAvatar>
                <UserInfo>
                  <UserName>{user.name}</UserName>
                  {(user.type || user.role || user.permission) && (
                    <UserRole>{user.type || user.role || user.permission}</UserRole>
                  )}
                </UserInfo>
              </UserItem>
            ))
          )}
        </ListItems>
      </ListPanel>

      {/* Controls */}
      <ControlsColumn>
        <CtrlBtn
          type="button"
          $primary
          title={t('user:groups.form.addUser')}
          onClick={moveHighlightedRight}
          disabled={leftHighlighted.size === 0}
        >
          ›
        </CtrlBtn>
        <CtrlBtn
          type="button"
          title={t('user:groups.form.removeUser')}
          onClick={moveHighlightedLeft}
          disabled={rightHighlighted.size === 0}
        >
          ‹
        </CtrlBtn>
        <CtrlBtn
          type="button"
          $text
          title={t('user:groups.form.addAllUsers')}
          onClick={moveAllRight}
          disabled={available.length === 0}
        >
          »
        </CtrlBtn>
        <CtrlBtn
          type="button"
          $text
          title={t('user:groups.form.removeAllUsers')}
          onClick={moveAllLeft}
          disabled={selected.length === 0}
        >
          «
        </CtrlBtn>
      </ControlsColumn>

      {/* Right panel — Selected */}
      <ListPanel $right>
        <ListPanelHeader>
          <ListPanelLabel>{t('user:groups.form.selected')}</ListPanelLabel>
        </ListPanelHeader>
        <TextField
          fullWidth
          value={rightSearch}
          onChange={e => setRightSearch(e.target.value)}
          placeholder={t('user:groups.form.searchPlaceholder')}
          sx={listSearchSx}
        />
        <ListItems>
          {filteredRight.length === 0 ? (
            <EmptyList>—</EmptyList>
          ) : (
            filteredRight.map(user => (
              <UserItem
                key={user._id}
                $highlighted={rightHighlighted.has(user._id)}
                $right
                onClick={() => toggleRight(user._id)}
              >
                <UserAvatar $gradient={getGradient(user.name)}>
                  <AvatarInitials>{getInitials(user.name)}</AvatarInitials>
                </UserAvatar>
                <UserInfo>
                  <UserName>{user.name}</UserName>
                  {(user.type || user.role || user.permission) && (
                    <UserRole>{user.type || user.role || user.permission}</UserRole>
                  )}
                </UserInfo>
              </UserItem>
            ))
          )}
        </ListItems>
      </ListPanel>
    </DualListbox>
  );
};

// ─── Main form component ──────────────────────────────────────────────────────

export const FormUsersGroup = ({
  register,
  handleSubmit,
  handleComplete,
  errors,
  control,
  availableUsers,
  selectedUsers,
  isLoadingUsers,
  onUsersChange,
}) => {
  const { t } = useTranslation(['user']);

  const onSubmit = () => handleComplete();

  return (
    <form id="users-group-form" onSubmit={handleSubmit(onSubmit)}>
      {/* Name field */}
      <FieldGroup data-tour="group-name">
        <FieldLabel htmlFor="group-name-input">
          {t('user:groups.form.name')}<RequiredStar> *</RequiredStar>
        </FieldLabel>
        <TextField
          id="group-name-input"
          fullWidth
          error={!!errors?.name}
          autoFocus
          placeholder={t('user:groups.form.namePlaceholder')}
          {...register('name', { required: t('user:groups.form.validation.nameRequired') })}
        />
        {errors?.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
      </FieldGroup>

      {/* Active toggle card */}
      <Controller
        control={control}
        name="active"
        render={({ field: { onChange, value } }) => (
          <ActiveCard data-tour="group-active">
            <Checkbox
              checked={!!value}
              onChange={e => onChange(e.target.checked)}
            />
            <ActiveTextWrap>
              <ActiveTitle>{t('user:groups.form.active')}</ActiveTitle>
              <ActiveDescription>{t('user:groups.form.activeDescription')}</ActiveDescription>
            </ActiveTextWrap>
          </ActiveCard>
        )}
      />

      {/* Users dual listbox */}
      <SectionTitle>{t('user:groups.form.groupUsersTitle')}</SectionTitle>
      <UserDualList
        available={availableUsers}
        selected={selectedUsers}
        onChange={onUsersChange}
        isLoading={isLoadingUsers}
        t={t}
      />
    </form>
  );
};
